import { Typography } from '@mui/joy';
import { lowerFirst } from 'lodash';
import { Dispatch, SetStateAction } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { useAuthUser } from 'auth/hooks';
import { SlidingPanel, Tabs } from 'core/components';
import { formatDate } from 'core/utils';
import { QuoteDocuments, QuoteNote } from 'quotes/components/QuoteDetails';
import { QuoteEntityType, QuoteNoteType, QuoteType } from 'quotes/enums';
import { VendorQuoteHelper } from 'quotes/helpers';
import { IVendorQuote } from 'quotes/types';
import { getQuoteNoteContent, getQuoteVersionNumber } from 'quotes/utils';
import { Color, Level } from 'theme/enums';

import { baseInfoWrapperStyle, SubtitleLabelWrapper, SubtitleValueWrapper } from './style';
import { Attributes, VendorQuoteBaseInformation } from './Tabs';

interface IProps {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  quote: IVendorQuote;
  entityType?: QuoteEntityType;
}

export const VendorQuoteDetails = ({
  isOpen,
  setIsOpen,
  quote,
  entityType = QuoteEntityType.QUOTE,
}: IProps) => {
  const { t } = useTranslation();

  const { user } = useAuthUser();

  const onClose = () => setIsOpen(false);

  const showAttributes =
    QuoteEntityType.QUOTE === entityType &&
    VendorQuoteHelper.isAttributesAvailable({ quote, user });

  const tabs = [
    {
      label: t('labelBaseInformation'),
      content: <VendorQuoteBaseInformation quote={quote} entityType={entityType} />,
      style: baseInfoWrapperStyle,
    },
    {
      label: t('labelQuoteNote'),
      content: (
        <QuoteNote
          quote={quote}
          type={QuoteNoteType.CUSTOMER_NOTE}
          entityType={entityType}
          quoteType={QuoteType.VENDOR}
          isEditable={false}
        />
      ),
      badgeProps: getQuoteNoteContent(quote, QuoteNoteType.CUSTOMER_NOTE)
        ? { color: Color.DANGER }
        : null,
      style: baseInfoWrapperStyle,
    },
    {
      label: t('labelDocuments'),
      content: <QuoteDocuments quote={quote} entityType={entityType} />,
      style: baseInfoWrapperStyle,
    },
    ...(showAttributes
      ? [
          {
            label: t('labelAttributes'),
            content: <Attributes quote={quote} />,
            style: baseInfoWrapperStyle,
          },
        ]
      : []),
  ];

  return (
    <SlidingPanel
      isOpen={isOpen}
      onClosePanel={onClose}
      title={t('titleQuoteDetails', {
        id: quote?.id,
        version: getQuoteVersionNumber(quote),
        entityType,
      })}
      subtitle={
        <Typography color={Color.NEUTRAL} level={Level.TITLE_SM}>
          <Trans
            i18nKey="subtitleLastUpdate"
            values={{ value: formatDate(quote?.updatedAt) }}
            components={{
              labelWrapper: <SubtitleLabelWrapper></SubtitleLabelWrapper>,
              valueWrapper: <SubtitleValueWrapper></SubtitleValueWrapper>,
            }}
          />
        </Typography>
      }
      autoWidth
    >
      <Tabs tabs={tabs} ariaLabel={`${lowerFirst(entityType)}Details`} />
    </SlidingPanel>
  );
};
